var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a, _b, _c, _d;
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
// Reusable component for rendering heatmap legend images
import { useContext } from "react";
import { useParams } from "react-router-dom";
import { DownloadableSection } from "../components/DownloadableSection/DownloadableSection";
import DrilldownFactorChart from "../components/DrilldownFactorChart";
import { getCohortColorForHeatmap, mapCohortColorsForHeatmap, } from "../components/LevelFunctionMatrix/Variants";
import { LevelledFramework } from "../components/LevelledFramework/LevelledFramework";
import { CBICapabilitiesLegendContent, CBIPotentialLegendContent, hoganHDSLegendContent, hoganHPILegendContent, hoganMVPILegendContent, ImmersiveAgilityLegendContent, ImmersiveMetaLegendContent, swiftLegendContent, waveLegendContent, } from "../pages/CohortInsightsReportPages/legendContentData";
import { useGetLeaderScoreAggByAssessmentQuery, useGetLeaderScoresAssessmentAggByFactorQuery, useGetLeaderScoresAssessmentAggBySkillGroupQuery, useGetLeaderScoresAssessmentAggBySkillsQuery, useGetSingleAvailableCohortReportsQuery, } from "../store/api/core";
import { AverageBarConfigMap, Colors, PageType, SwiftSkill, Tab, getRiskLevelDescription, } from "./cohortHelpers";
import { FilterContext } from "../providers/FilterProvider";
import { getColoredScore, getDynamicColumnHeader, getScoreDescription, getScoreLevelDescription, nameDefinition, percentileScore, scoreDifferenceColumnHeader, targetScoreColumnHeader, useColumnedTableReportData, } from "./cohortTableColumns";
import { getDescriptionDecider } from "./action";
import { ValidSortDirections } from "../components/Table/Table";
import { ScoreFormatter } from "../pages/AssessmentPage/AssessmentPage";
import CohortInsightsSkillsTable from "../components/CohortInsightsSkillsTable";
import { getRoute } from "../routes/getAllRoutes";
import styles from "../components/CohortSkillGroupV2/CohortSkillGroupV2.module.scss";
import { LegendImages } from "../components/CohortSkillGroupV2/CohortSkillGroupV2";
import { HeatmapTitle } from "../components/HeatmapTitle/HeatmapTitle";
import { CohortLeaderTableWrapper } from "../components/CohortInsights/Restrictions";
import ImmersiveAgilityDrilldownFactorChart from "../components/ImmersiveAgilityDrilldownFactorChart";
var decidePopupCircleBorder = function (tab) { return function (score) {
    return Colors[tab][0].high > score ? Colors[tab][0].border : "transparent";
}; };
export var labelMap = {
    averageScoreLabel: (_a = {},
        _a[Tab.CBI] = "Average Score",
        _a[Tab.SavilleWave] = "Average Sten Score",
        _a[Tab.SavilleSwiftAptitude] = "Average Sten Score",
        _a[Tab.HoganHDS] = "Average Percentile Score",
        _a[Tab.HoganHPI] = "Average Percentile Score",
        _a[Tab.HoganMVPI] = "Average Percentile Score",
        _a[Tab.ImmersiveAgility] = "Average Score",
        _a[Tab.ImmersiveMeta] = "Average Score",
        _a),
    distributionPercentageLabel: (_b = {},
        _b[Tab.CBI] = "Percent Score Distribution",
        _b[Tab.SavilleWave] = "Sten Score Distribution",
        _b[Tab.SavilleSwiftAptitude] = "Sten Score Distribution",
        _b[Tab.HoganHDS] = "Percentile Score Distribution",
        _b[Tab.HoganHPI] = "Percentile Score Distribution",
        _b[Tab.HoganMVPI] = "Percentile Score Distribution",
        _b[Tab.ImmersiveAgility] = "Percent Score Distribution",
        _b[Tab.ImmersiveMeta] = "Score Distribution",
        _b),
};
var DrilldownFactors = function (_a) {
    var _b, _c;
    var category = _a.category, skillGroup = _a.skillGroup;
    var allFilters = useContext(FilterContext).getFilters;
    var reportId = useParams().reportId;
    var data = useGetLeaderScoresAssessmentAggByFactorQuery({
        filters: allFilters(),
        reportId: reportId || "",
        category: category,
    });
    var factorScores = (_c = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c[skillGroup];
    var colors = Colors[category] || [];
    var drillDownFactorData = {
        start: AverageBarConfigMap[category].start,
        end: AverageBarConfigMap[category].end,
        scale: AverageBarConfigMap[category].scale,
        colors: colors,
        skillGroupName: skillGroup,
        capabilititesFactorData: factorScores,
        potentialFactorData: factorScores,
        pageType: PageType[category],
        averageScoreLabel: "",
        distributionPercentageLabel: "",
        category: category,
    };
    drillDownFactorData.averageScoreLabel =
        labelMap.averageScoreLabel[category] || "Average score";
    drillDownFactorData.distributionPercentageLabel =
        labelMap.distributionPercentageLabel[category] ||
            "Percent score distribution";
    return (_jsx(DownloadableSection, __assign({ filename: "factor-score.png" }, { children: _jsx(DrilldownFactorChart, __assign({}, drillDownFactorData)) })));
};
var ImmersiveAgilityDrilldownFactors = function (_a) {
    var _b, _c;
    var category = _a.category, skillGroup = _a.skillGroup;
    var allFilters = useContext(FilterContext).getFilters;
    var reportId = useParams().reportId;
    var data = useGetLeaderScoresAssessmentAggByFactorQuery({
        filters: allFilters(),
        reportId: reportId || "",
        category: category,
    });
    var factorScores = (_c = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c[skillGroup];
    var colors = Colors[category] || [];
    var drillDownFactorData = {
        start: AverageBarConfigMap[category].start,
        end: AverageBarConfigMap[category].end,
        scale: AverageBarConfigMap[category].scale,
        colors: colors,
        skillGroupName: skillGroup,
        factorsData: factorScores,
        averageScoreLabel: labelMap.averageScoreLabel[category] || "Average Score",
        distributionPercentageLabel: labelMap.distributionPercentageLabel[category] ||
            "Percent Score Distribution",
    };
    return (_jsx(DownloadableSection, __assign({ filename: "factor-score.png" }, { children: _jsx(ImmersiveAgilityDrilldownFactorChart, __assign({}, drillDownFactorData)) })));
};
var DrilldownSkillGroupTable = function (_a) {
    var _b, _c, _d, _e;
    var skillGroup = _a.skillGroup, columnGetter = _a.columnGetter, category = _a.category;
    var reportId = useParams().reportId;
    var report = useGetSingleAvailableCohortReportsQuery({
        reportId: reportId || "",
    });
    var column1Attribute = (_c = (_b = report === null || report === void 0 ? void 0 : report.data) === null || _b === void 0 ? void 0 : _b.leaderTableOptions) === null || _c === void 0 ? void 0 : _c.column1Attribute;
    var column2Attribute = (_e = (_d = report === null || report === void 0 ? void 0 : report.data) === null || _d === void 0 ? void 0 : _d.leaderTableOptions) === null || _e === void 0 ? void 0 : _e.column2Attribute;
    var cohortHeatmapTableData = useColumnedTableReportData({
        useTableData: useGetLeaderScoresAssessmentAggBySkillGroupQuery,
        reportId: reportId || "",
        skillGroup: skillGroup,
        category: category,
    });
    return (_jsx(CohortLeaderTableWrapper, { children: _jsx("div", __assign({ style: { padding: "32px" } }, { children: cohortHeatmapTableData && (_jsx(CohortInsightsSkillsTable, { data: cohortHeatmapTableData, title: "".concat(skillGroup || "", " Scores by Leader"), filename: "swift-leaders-table.png", getColumnDescriptions: columnGetter, column1Attribute: column1Attribute, column2Attribute: column2Attribute })) })) }));
};
var hoganPercentileBarChartProps = {
    range: { minimum: -0.5, maximum: 100.5 },
    xTicks: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
    formatAverageScore: function (value) {
        var _a, _b;
        return ({
            text: "".concat((_b = (_a = Number(value)) === null || _a === void 0 ? void 0 : _a.toFixed(0)) !== null && _b !== void 0 ? _b : "", "%"),
            formatting: {
                paddingLeft: "6px",
            },
        });
    },
    formatScore: function (score) { var _a, _b; return "".concat((_b = (_a = Number(score)) === null || _a === void 0 ? void 0 : _a.toFixed(0)) !== null && _b !== void 0 ? _b : "", "%"); },
    calculateBarWidth: function (allowed) { return allowed / 10 - 3; },
};
var STEN_SCORE_X_TICKS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
var calculateReducedBarWidth = function (allowed) { return allowed - 3; };
var formatAverageScoreToOneDecimal = function (value) {
    var _a, _b;
    return ({
        text: (_b = (_a = Number(value)) === null || _a === void 0 ? void 0 : _a.toFixed(1)) !== null && _b !== void 0 ? _b : "",
        formatting: {
            paddingLeft: "6px",
        },
    });
};
var hoganPercentileSkillBarChartProps = __assign(__assign({}, hoganPercentileBarChartProps), { calculateBarWidth: calculateReducedBarWidth });
var immersiveAgilitySkillBarChartProps = {
    range: { minimum: 0.5, maximum: 5.5 },
    xTicks: [1, 2, 3, 4, 5],
    formatAverageScore: formatAverageScoreToOneDecimal,
    calculateBarWidth: calculateReducedBarWidth,
    decidePopupCircleBorder: decidePopupCircleBorder(Tab.ImmersiveAgility),
};
var getNormLegendImage = function (key, includeLabel) {
    if (includeLabel === void 0) { includeLabel = false; }
    var normLegendImages = {
        above: {
            src: getRoute("AboveNorm"),
            alt: "Above Norm",
            label: "Above Norm",
        },
        at: {
            src: getRoute("AtNorm"),
            alt: "At Norm",
            label: "At Norm",
        },
        below: {
            src: getRoute("BelowNorm"),
            alt: "Below Norm",
            label: "Below Norm",
        },
    };
    return __assign(__assign({ key: key, src: normLegendImages[key].src, alt: normLegendImages[key].alt }, (includeLabel ? { label: normLegendImages[key].label } : {})), { className: styles.normImage });
};
var hoganNormLegendImageItems = [
    getNormLegendImage("above", true),
    getNormLegendImage("at", true),
    getNormLegendImage("below", true),
];
var getHoganNormLegendImages = function (score) {
    if (score > 60) {
        return _jsx(LegendImages, { images: [getNormLegendImage("above")] });
    }
    if (score >= 40 && score <= 60) {
        return _jsx(LegendImages, { images: [getNormLegendImage("at")] });
    }
    if (score < 40) {
        return _jsx(LegendImages, { images: [getNormLegendImage("below")] });
    }
    return null;
};
var getStenNormLegendImages = function (score) {
    if (score > 6) {
        return _jsx(LegendImages, { images: [getNormLegendImage("above")] });
    }
    if (score >= 5 && score <= 6) {
        return _jsx(LegendImages, { images: [getNormLegendImage("at")] });
    }
    if (score < 5) {
        return _jsx(LegendImages, { images: [getNormLegendImage("below")] });
    }
    return null;
};
var getImmersiveAgilityLegendImages = function (score) {
    if (score > 3.65) {
        return _jsx(LegendImages, { images: [getNormLegendImage("above")] });
    }
    if (score >= 3.25 && score <= 3.65) {
        return _jsx(LegendImages, { images: [getNormLegendImage("at")] });
    }
    if (score < 3.25) {
        return _jsx(LegendImages, { images: [getNormLegendImage("below")] });
    }
    return null;
};
var noColorsHeatmapColors = {
    colors: mapCohortColorsForHeatmap(Colors[Tab.NoColors], Colors[Tab.NoColors].reduce(function (accumilator, current) {
        var _a;
        return (__assign(__assign({}, accumilator), (_a = {}, _a[current.color] = current.text, _a)));
    }, {})),
    chooser: getCohortColorForHeatmap(Colors[Tab.NoColors]),
};
var stenNormHeatmapTitle = (_jsx(HeatmapTitle, { title: "Average Sten Scores", legendImageItems: hoganNormLegendImageItems }));
var commonHDSSkill = {
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    secondaryTitle: "HDS Risk Levels",
    colors: Colors[Tab.HoganHDS],
    skillGroupDonutTitle: "Average Percentile Score Distribution",
    heatmapDrilldownDonutTitle: "Average Percentile Score Distribution",
    skillDonutTitle: "",
    heatmapTitle: (_jsx(HeatmapTitle, { title: "Average Percentile Scores", legendImageItems: [
            {
                key: "above",
                src: getRoute("AboveNormCircle"),
                alt: "Above Norm Circle",
                label: "Above Norm",
                className: styles.aboveNormCircleImage,
            },
        ] })),
    heatmapColors: noColorsHeatmapColors,
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " % Scores"); },
    skillBarChartTitle: function (skillGroup) {
        return "Spread of ".concat(skillGroup, " % Scores");
    },
    legendContent: _jsx(LevelledFramework, { legendContent: hoganHDSLegendContent }),
    heatmapLegendImages: function (score) {
        return score > 65 ? (_jsx(LegendImages, { images: [
                {
                    key: "above",
                    src: getRoute("AboveNormCircle"),
                    alt: "Above Norm Circle",
                    className: styles.aboveNormCircleImage,
                },
            ] })) : null;
    },
    barChartProps: hoganPercentileBarChartProps,
    skillBarChartProps: hoganPercentileSkillBarChartProps,
};
var commonHPISkill = {
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    secondaryTitle: "HPI Scoring Scale",
    colors: Colors[Tab.HoganHPI],
    skillGroupDonutTitle: "Average Percentile Score Distribution",
    heatmapDrilldownDonutTitle: "Average Percentile Score Distribution",
    skillDonutTitle: "",
    heatmapTitle: (_jsx(HeatmapTitle, { title: "Average Percentile Scores", legendImageItems: hoganNormLegendImageItems })),
    heatmapColors: noColorsHeatmapColors,
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " % Scores"); },
    skillBarChartTitle: function (skillGroup) {
        return "Spread of ".concat(skillGroup, " % scores");
    },
    legendContent: _jsx(LevelledFramework, { legendContent: hoganHPILegendContent }),
    heatmapLegendImages: getHoganNormLegendImages,
    barChartProps: hoganPercentileBarChartProps,
    skillBarChartProps: hoganPercentileSkillBarChartProps,
};
var commonMVPISkill = {
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    secondaryTitle: "MVPI Scoring Scale",
    colors: Colors[Tab.HoganMVPI],
    skillGroupDonutTitle: "Average Percentile Score Distribution",
    heatmapDrilldownDonutTitle: "Average Percentile Score Distribution",
    skillDonutTitle: "",
    heatmapTitle: (_jsx(HeatmapTitle, { title: "Average Percentile Scores", legendImageItems: hoganNormLegendImageItems })),
    heatmapLegendImages: getHoganNormLegendImages,
    heatmapColors: noColorsHeatmapColors,
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " % Scores"); },
    skillBarChartTitle: function (skillGroup) {
        return "Spread of ".concat(skillGroup, " % Scores");
    },
    barChartProps: hoganPercentileBarChartProps,
    skillBarChartProps: hoganPercentileSkillBarChartProps,
    legendContent: _jsx(LevelledFramework, { legendContent: hoganMVPILegendContent }),
};
var commonSwiftSkill = {
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    secondaryTitle: "Saville Swift Aptitude",
    colors: Colors[Tab.SavilleSwiftAptitude],
    bubbleChartFields: {
        x: { label: "Aptitude", key: "Aptitude" },
        y: { label: "Pace", key: "Pace" },
    },
    skillGroupDonutTitle: "Sten Score Distribution",
    heatmapDrilldownDonutTitle: "Average Sten Score Distribution",
    skillDonutTitle: "Sten Score Distribution",
    heatmapTitle: stenNormHeatmapTitle,
    heatmapLegendImages: getStenNormLegendImages,
    showHeatmapScorePercentage: false,
    heatmapColors: noColorsHeatmapColors,
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Sten Scores"); },
    skillBarChartTitle: function (skillGroup) {
        return "Spread of ".concat(skillGroup, " Sten Scores");
    },
    legendContent: _jsx(LevelledFramework, { legendContent: swiftLegendContent }),
    barChartProps: {
        range: { minimum: 0.5, maximum: 10.5 },
        xTicks: STEN_SCORE_X_TICKS,
        formatAverageScore: formatAverageScoreToOneDecimal,
        decidePopupCircleBorder: decidePopupCircleBorder(Tab.SavilleSwiftAptitude),
    },
    skillBarChartProps: {
        range: { minimum: 0.5, maximum: 10.5 },
        xTicks: STEN_SCORE_X_TICKS,
        calculateBarWidth: calculateReducedBarWidth,
        formatAverageScore: formatAverageScoreToOneDecimal,
        decidePopupCircleBorder: decidePopupCircleBorder(Tab.SavilleSwiftAptitude),
    },
};
var commonCbi = {
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    colors: Colors[Tab.CBI],
    bubbleChartFields: {
        x: { label: "Leadership Capabilities (MET)", key: "Capabilities" },
        y: { label: "Agility & Potential (A)", key: "Potential" },
    },
    skillGroupDonutTitle: "Average Score Distribution",
    heatmapDrilldownDonutTitle: "Average Score Distribution",
    skillDonutTitle: "Score Distribution",
    heatmapTitle: _jsx(HeatmapTitle, { title: "Average Scores" }),
    heatmapColors: {
        colors: mapCohortColorsForHeatmap(Colors[Tab.CBI], Colors[Tab.CBI].reduce(function (accumilator, current) {
            var _a;
            return (__assign(__assign({}, accumilator), (_a = {}, _a[current.color] = current.text, _a)));
        }, {})),
        chooser: getCohortColorForHeatmap(Colors[Tab.CBI]),
    },
    legendContent: (_jsx(LevelledFramework, { legendContent: CBIPotentialLegendContent })),
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Scores"); },
    skillBarChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Scores"); },
    barChartProps: {
        range: { minimum: 0.95, maximum: 5.05 },
    },
    showHeatmapScorePercentage: false,
    skillBarChartProps: {
        range: { minimum: 0.5, maximum: 5.5 },
        calculateBarWidth: calculateReducedBarWidth,
        xTicks: [1, 2, 3, 4, 5],
        formatAverageScore: formatAverageScoreToOneDecimal,
    },
};
var commonWave = {
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    secondaryTitle: "Saville Wave",
    colors: Colors[Tab.SavilleWave],
    bubbleChartFields: {
        x: { label: "Leadership Capabilities (MET)", key: "Capabilities" },
        y: { label: "Agility & Potential (A)", key: "Potential" },
    },
    skillGroupDonutTitle: "Sten Score Distribution",
    heatmapDrilldownDonutTitle: "Average Sten Score Distribution",
    skillDonutTitle: "Average Sten Score Distribution",
    heatmapTitle: stenNormHeatmapTitle,
    heatmapLegendImages: getStenNormLegendImages,
    showHeatmapScorePercentage: false,
    heatmapColors: noColorsHeatmapColors,
    legendContent: _jsx(LevelledFramework, { legendContent: waveLegendContent }),
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Sten Scores"); },
    skillBarChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Scores"); },
    barChartProps: {
        range: { minimum: 0.5, maximum: 10.5 },
        xTicks: STEN_SCORE_X_TICKS,
        formatAverageScore: formatAverageScoreToOneDecimal,
        decidePopupCircleBorder: decidePopupCircleBorder(Tab.SavilleWave),
    },
    skillBarChartProps: {
        range: { minimum: 0.5, maximum: 10.5 },
        xTicks: STEN_SCORE_X_TICKS,
        calculateBarWidth: calculateReducedBarWidth,
        formatAverageScore: formatAverageScoreToOneDecimal,
        decidePopupCircleBorder: decidePopupCircleBorder(Tab.SavilleWave),
    },
};
var commonImmersiveMeta = {
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    colors: Colors[Tab.ImmersiveMeta],
    bubbleChartFields: {
        x: { label: "Leadership Capabilities (MET)", key: "Capabilities" },
        y: { label: "Agility & Potential (A)", key: "Potential" },
    },
    skillGroupDonutTitle: "Score Distribution",
    heatmapDrilldownDonutTitle: "Average Score Distribution",
    skillDonutTitle: "Score Distribution",
    heatmapTitle: stenNormHeatmapTitle,
    heatmapColors: noColorsHeatmapColors,
    heatmapLegendImages: getImmersiveAgilityLegendImages,
    legendContent: (_jsx(LevelledFramework, { legendContent: CBIPotentialLegendContent })),
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Scores"); },
    skillBarChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Scores"); },
    barChartProps: {
        range: { minimum: 0.95, maximum: 5.05 },
        decidePopupCircleBorder: decidePopupCircleBorder(Tab.ImmersiveMeta),
    },
    showHeatmapScorePercentage: false,
    skillBarChartProps: {
        range: { minimum: 0.5, maximum: 5.5 },
        calculateBarWidth: calculateReducedBarWidth,
        xTicks: [1, 2, 3, 4, 5],
        formatAverageScore: formatAverageScoreToOneDecimal,
        decidePopupCircleBorder: decidePopupCircleBorder(Tab.ImmersiveMeta),
    },
};
var heatmapSwiftOverallColumnDescription = function (dataKey) { return function () {
    return [
        nameDefinition,
        getColoredScore("Sten Score", Colors[Tab.SavilleSwiftAptitude], function (row) {
            var _a;
            return ({
                sort: row.sten_score,
                data: {
                    score: typeof row.sten_score === "number"
                        ? (_a = row.sten_score) === null || _a === void 0 ? void 0 : _a.toFixed(0)
                        : "0.0",
                },
            });
        }),
        percentileScore,
        getScoreLevelDescription("Score Category", getDescriptionDecider(Colors[Tab.SavilleSwiftAptitude], dataKey)),
        {
            header: "Verbal Sten",
            defaultSortDirection: ValidSortDirections.ASC,
            formatter: ScoreFormatter,
            valueTransformer: function (row) {
                var _a, _b;
                return ({
                    sort: (_a = row.verbal_sten) === null || _a === void 0 ? void 0 : _a.toFixed(0),
                    data: {
                        score: (_b = row.verbal_sten) === null || _b === void 0 ? void 0 : _b.toFixed(0),
                    },
                });
            },
        },
        {
            header: "Numerical Sten",
            defaultSortDirection: ValidSortDirections.ASC,
            formatter: ScoreFormatter,
            valueTransformer: function (row) {
                var _a, _b;
                return ({
                    sort: (_a = row.numerical_sten) === null || _a === void 0 ? void 0 : _a.toFixed(0),
                    data: {
                        score: (_b = row.numerical_sten) === null || _b === void 0 ? void 0 : _b.toFixed(0),
                    },
                });
            },
        },
        {
            header: "Abstract Sten",
            defaultSortDirection: ValidSortDirections.ASC,
            formatter: ScoreFormatter,
            valueTransformer: function (row) {
                var _a, _b;
                return ({
                    sort: (_a = row.abstract_sten) === null || _a === void 0 ? void 0 : _a.toFixed(0),
                    data: {
                        score: (_b = row.abstract_sten) === null || _b === void 0 ? void 0 : _b.toFixed(0),
                    },
                });
            },
        },
    ];
}; };
var commonImmersiveAgility = {
    skillGroupDonutTitle: "Average Score Distribution",
    colors: Colors[Tab.ImmersiveAgility],
    useTableData: useGetLeaderScoresAssessmentAggBySkillsQuery,
    useChartData: useGetLeaderScoreAggByAssessmentQuery,
    secondaryTitle: "Immersive Agility",
    heatmapDrilldownDonutTitle: "Average Score Distribution",
    skillDonutTitle: "Score Distribution",
    heatmapTitle: stenNormHeatmapTitle,
    heatmapLegendImages: getImmersiveAgilityLegendImages,
    heatmapColors: noColorsHeatmapColors,
    barChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Scores"); },
    skillBarChartTitle: function (skillGroup) { return "Spread of ".concat(skillGroup, " Scores"); },
    showHeatmapScorePercentage: false,
    skillBarChartProps: immersiveAgilitySkillBarChartProps,
    barChartProps: immersiveAgilitySkillBarChartProps,
    legendContent: (_jsx(LevelledFramework, { legendContent: ImmersiveAgilityLegendContent })),
};
var heatmapWaveColumnDescription = function (dataKey) { return function (column1Attribute, column2Attribute) {
    return __spreadArray(__spreadArray([
        nameDefinition,
        getColoredScore("Average Sten Score", Colors[Tab.SavilleWave], function (row) {
            var _a;
            return ({
                sort: typeof row.averageScore === "number" ? row.averageScore : 0,
                data: {
                    score: typeof row.averageScore === "number"
                        ? (_a = row.averageScore) === null || _a === void 0 ? void 0 : _a.toFixed(1)
                        : "0.0",
                },
            });
        }),
        getScoreLevelDescription("Score Category", getDescriptionDecider(Colors[Tab.SavilleWave], dataKey))
    ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
}; };
var immersiveAndAgilityColumnDescription = function (dataKey, key, format) {
    if (format === void 0) { format = function (s) { return "".concat(s); }; }
    return function (column1Attribute, column2Attribute) {
        return __spreadArray(__spreadArray([
            nameDefinition,
            getColoredScore("Score", Colors[Tab.ImmersiveAgility], function (row) { return ({
                sort: "".concat(row[key] || ""),
                data: {
                    score: row[key] ? format(row[key]) : "0",
                },
            }); })
        ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
    };
};
var heatmapCbiColumnDescription = function (dataKey) { return function (column1Attribute, column2Attribute) {
    return __spreadArray(__spreadArray([
        nameDefinition,
        getColoredScore("Average Score", Colors[Tab.CBI], function (row) {
            var _a, _b;
            return ({
                sort: "".concat(row.averageScore || ""),
                data: {
                    score: (_b = (_a = row === null || row === void 0 ? void 0 : row.averageScore) === null || _a === void 0 ? void 0 : _a.toFixed(1)) !== null && _b !== void 0 ? _b : "",
                },
            });
        }),
        getScoreLevelDescription("Average Experience Level", getDescriptionDecider(Colors[Tab.CBI], dataKey))
    ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
}; };
var skillWaveColumnDescription = function (skillGroup) {
    return function (column1Attribute, column2Attribute) {
        return __spreadArray(__spreadArray([
            nameDefinition,
            getColoredScore("Sten Score", Colors[Tab.SavilleWave], function (row) {
                var _a;
                return ({
                    sort: row.score,
                    data: {
                        score: (_a = row === null || row === void 0 ? void 0 : row.score) === null || _a === void 0 ? void 0 : _a.toFixed(0),
                    },
                });
            }),
            getScoreDescription("Score Category", getDescriptionDecider(Colors[Tab.SavilleWave], skillGroup))
        ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
    };
};
var skillSwiftColumnDescription = function (skillGroup) {
    return function (column1Attribute, column2Attribute) {
        return __spreadArray(__spreadArray([
            nameDefinition,
            getColoredScore("Sten Score", Colors[Tab.SavilleSwiftAptitude], function (row) {
                var _a;
                return ({
                    sort: row.sten_score,
                    data: {
                        score: typeof row.sten_score === "number"
                            ? (_a = row.sten_score) === null || _a === void 0 ? void 0 : _a.toFixed(0)
                            : "0.0",
                    },
                });
            }),
            percentileScore,
            getScoreLevelDescription("Score Category", getDescriptionDecider(Colors[Tab.SavilleSwiftAptitude], skillGroup))
        ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
    };
};
var skillCbiColumnDescription = function (skillGroup) {
    return function (column1Attribute, column2Attribute) {
        return __spreadArray(__spreadArray([
            nameDefinition,
            getColoredScore("Score", Colors[Tab.CBI], function (row) {
                var _a;
                return ({
                    sort: "".concat(row.score || ""),
                    data: {
                        score: (_a = row === null || row === void 0 ? void 0 : row.score) === null || _a === void 0 ? void 0 : _a.toFixed(0),
                    },
                });
            }),
            targetScoreColumnHeader,
            scoreDifferenceColumnHeader,
            getScoreLevelDescription("Experience Level", getDescriptionDecider(Colors[Tab.CBI], skillGroup))
        ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
    };
};
var heatmapImmersiveMetaColumnDescription = function () { return function (column1Attribute, column2Attribute) {
    return __spreadArray(__spreadArray([
        nameDefinition,
        getColoredScore("Score", Colors[Tab.ImmersiveMeta], function (row) {
            var _a, _b;
            return ({
                sort: "".concat(row.averageScore || ""),
                data: {
                    score: (_b = (_a = row === null || row === void 0 ? void 0 : row.averageScore) === null || _a === void 0 ? void 0 : _a.toFixed(1)) !== null && _b !== void 0 ? _b : "",
                },
            });
        })
    ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
}; };
var skillImmersiveMetaColumnDescription = function () { return function (column1Attribute, column2Attribute) {
    return __spreadArray(__spreadArray([
        nameDefinition,
        getColoredScore("Score", Colors[Tab.ImmersiveMeta], function (row) {
            var _a;
            return ({
                sort: "".concat(row.score || ""),
                data: {
                    score: (_a = row === null || row === void 0 ? void 0 : row.score) === null || _a === void 0 ? void 0 : _a.toFixed(0),
                },
            });
        })
    ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
}; };
var hoganSkillsColumnDescription = function (category) { return function (column1Attribute, column2Attribute) {
    return __spreadArray(__spreadArray([
        nameDefinition,
        getColoredScore("Percentile Score", Colors[category], function (row) {
            var _a;
            return ({
                sort: row.score,
                data: {
                    score: typeof row.score === "number" ? (_a = row.score) === null || _a === void 0 ? void 0 : _a.toFixed(0) : "0",
                },
            });
        }, true),
        {
            header: "Risk Level",
            formatter: ScoreFormatter,
            valueTransformer: function (row) { return ({
                sort: getRiskLevelDescription(row.score_level, category),
                data: { score: getRiskLevelDescription(row.score_level, category) },
            }); },
        }
    ], (column1Attribute ? [getDynamicColumnHeader(column1Attribute)] : []), true), (column2Attribute ? [getDynamicColumnHeader(column2Attribute)] : []), true);
}; };
// Dynamic config generator for Hogan HDS (since skill names are not known at build time)
export var getHoganHDSConfig = function (skill) { return (__assign(__assign({ backTitle: "Hogan HDS: ".concat(skill), backUrl: "Hogan HDS: ".concat(skill) }, commonHDSSkill), { skillGroupDisplayName: skill, drilldownBottomComponent: function () { return (_jsx(DrilldownSkillGroupTable, { category: Tab.HoganHDS, skillGroup: skill, columnGetter: hoganSkillsColumnDescription(Tab.HoganHDS) })); }, heatmapDrilldownColumns: hoganSkillsColumnDescription(Tab.HoganHDS), distributionSkillGroup: "HDS Percentile Scale Scores", distributionSkillName: skill })); };
export var getHoganHPIConfig = function (skill) { return (__assign(__assign({ backTitle: "Hogan HPI: ".concat(skill), backUrl: "Hogan HPI: ".concat(skill) }, commonHPISkill), { skillGroupDisplayName: skill, drilldownBottomComponent: function () { return (_jsx(DrilldownSkillGroupTable, { category: Tab.HoganHPI, skillGroup: skill, columnGetter: hoganSkillsColumnDescription(Tab.HoganHPI) })); }, heatmapDrilldownColumns: hoganSkillsColumnDescription(Tab.HoganHPI), distributionSkillGroup: "HPI Percentile Scale Scores", distributionSkillName: skill })); };
export var getHoganMVPIConfig = function (skill) { return (__assign(__assign({ backTitle: "Hogan MVPI: ".concat(skill), backUrl: "Hogan MVPI: ".concat(skill) }, commonMVPISkill), { skillGroupDisplayName: skill, drilldownBottomComponent: function () { return (_jsx(DrilldownSkillGroupTable, { category: Tab.HoganMVPI, skillGroup: skill, columnGetter: hoganSkillsColumnDescription(Tab.HoganMVPI) })); }, heatmapDrilldownColumns: hoganSkillsColumnDescription(Tab.HoganMVPI), distributionSkillGroup: "MVPI Percentile Scale Scores", distributionSkillName: skill })); };
export var cohortSet = (_c = {},
    _c[Tab.CBI] = {
        Potential: __assign(__assign({ backTitle: "Final Assessor Rating: Agility & Potential", secondaryTitle: "Final Assessor Rating", backUrl: "CohortCategorizedSkill" }, commonCbi), { skillGroupDisplayName: "Agility & Potential", heatmapDrilldownColumns: heatmapCbiColumnDescription("Potential"), skillDrilldownColumns: skillCbiColumnDescription("Potential"), drilldownBottomComponent: function () { return (_jsx(DrilldownFactors, { category: Tab.CBI, skillGroup: "Potential" })); }, legendContent: (_jsx(LevelledFramework, { legendContent: CBIPotentialLegendContent })) }),
        Capabilities: __assign(__assign({ backTitle: "Final Assessor Rating: Leadership Capabilities", secondaryTitle: "Final Assessor Rating", backUrl: "CohortCategorizedSkill" }, commonCbi), { skillGroupDisplayName: "Leadership Capabilities", heatmapDrilldownColumns: heatmapCbiColumnDescription("Capabilities"), skillDrilldownColumns: skillCbiColumnDescription("Capabilities"), drilldownBottomComponent: function () { return (_jsx(DrilldownFactors, { category: Tab.CBI, skillGroup: "Capabilities" })); }, legendContent: (_jsx(LevelledFramework, { legendContent: CBICapabilitiesLegendContent })) }),
    },
    _c[Tab.ImmersiveMeta] = {
        Potential: __assign(__assign({ backTitle: "Immersive Meta: Agility & Potential", secondaryTitle: "Immersive META", backUrl: "CohortCategorizedSkill" }, commonImmersiveMeta), { skillGroupDisplayName: "Agility & Potential", heatmapDrilldownColumns: heatmapImmersiveMetaColumnDescription("Potential"), skillDrilldownColumns: skillImmersiveMetaColumnDescription("Potential"), drilldownBottomComponent: function () { return (_jsx(DrilldownFactors, { category: Tab.ImmersiveMeta, skillGroup: "Potential" })); }, legendContent: (_jsx(LevelledFramework, { legendContent: ImmersiveMetaLegendContent })) }),
        Capabilities: __assign(__assign({ backTitle: "Immersive Meta: Leadership Capabilities", secondaryTitle: "Immersive META", backUrl: "CohortCategorizedSkill" }, commonImmersiveMeta), { skillGroupDisplayName: "Leadership Capabilities", heatmapDrilldownColumns: heatmapImmersiveMetaColumnDescription("Capabilities"), skillDrilldownColumns: skillImmersiveMetaColumnDescription("Capabilities"), drilldownBottomComponent: function () { return (_jsx(DrilldownFactors, { category: Tab.ImmersiveMeta, skillGroup: "Capabilities" })); }, legendContent: (_jsx(LevelledFramework, { legendContent: ImmersiveMetaLegendContent })) }),
    },
    _c[Tab.SavilleWave] = {
        Potential: __assign(__assign({ backTitle: "Saville Wave: Agility & Potential", backUrl: "CohortCategorizedSkill" }, commonWave), { skillGroupDisplayName: "Agility & Potential", heatmapDrilldownColumns: heatmapWaveColumnDescription("Potential"), skillDrilldownColumns: skillWaveColumnDescription("Potential"), drilldownBottomComponent: function () { return (_jsx(DrilldownFactors, { category: Tab.SavilleWave, skillGroup: "Potential" })); } }),
        Capabilities: __assign(__assign({ backTitle: "Saville Wave: Leadership Capabilities", backUrl: "CohortCategorizedSkill" }, commonWave), { skillGroupDisplayName: "Leadership Capabilities", heatmapDrilldownColumns: heatmapWaveColumnDescription("Capabilities"), skillDrilldownColumns: skillWaveColumnDescription("Capabilities"), drilldownBottomComponent: function () { return (_jsx(DrilldownFactors, { category: Tab.SavilleWave, skillGroup: "Capabilities" })); } }),
    },
    _c[Tab.SavilleSwiftAptitude] = (_d = {},
        _d[SwiftSkill.Overall] = __assign(__assign({ backTitle: "Saville Swift Aptitude: Overall Score", backUrl: "Saville Swift Aptitude: Overall" }, commonSwiftSkill), { heatmapDrilldownColumns: heatmapSwiftOverallColumnDescription(SwiftSkill.Overall), skillDrilldownColumns: heatmapSwiftOverallColumnDescription(SwiftSkill.Overall), skillGroupDisplayName: "Overall", drilldownBottomComponent: function () { return (_jsx(CohortLeaderTableWrapper, { children: _jsx(DrilldownSkillGroupTable, { category: Tab.SavilleSwiftAptitude, skillGroup: SwiftSkill.Overall, columnGetter: heatmapSwiftOverallColumnDescription(SwiftSkill.Overall) }) })); } }),
        _d[SwiftSkill.Numerical] = __assign(__assign({ backTitle: "Saville Swift Aptitude: Numerical Reasoning", backUrl: "Saville Swift Aptitude: Numerical" }, commonSwiftSkill), { heatmapDrilldownColumns: skillSwiftColumnDescription(SwiftSkill.Numerical), skillGroupDisplayName: "Numerical Reasoning", skillDrilldownColumns: skillSwiftColumnDescription(SwiftSkill.Numerical), drilldownBottomComponent: function (confidential) {
                return confidential ? undefined : (_jsx(DrilldownSkillGroupTable, { category: Tab.SavilleSwiftAptitude, skillGroup: SwiftSkill.Numerical, columnGetter: skillSwiftColumnDescription(SwiftSkill.Numerical) }));
            } }),
        _d[SwiftSkill.Abstract] = __assign(__assign({ backTitle: "Saville Swift Aptitude: Abstract Reasoning", backUrl: "Saville Swift Aptitude: Abstract" }, commonSwiftSkill), { skillGroupDisplayName: "Abstract Reasoning", heatmapDrilldownColumns: skillSwiftColumnDescription(SwiftSkill.Abstract), skillDrilldownColumns: skillSwiftColumnDescription(SwiftSkill.Abstract), drilldownBottomComponent: function (confidential) {
                return confidential ? undefined : (_jsx(DrilldownSkillGroupTable, { category: Tab.SavilleSwiftAptitude, skillGroup: SwiftSkill.Abstract, columnGetter: skillSwiftColumnDescription(SwiftSkill.Abstract) }));
            } }),
        _d[SwiftSkill.Verbal] = __assign(__assign({ backTitle: "Saville Swift Aptitude: Verbal Reasoning", backUrl: "Saville Swift Aptitude: Verbal" }, commonSwiftSkill), { skillGroupDisplayName: "Verbal Reasoning", heatmapDrilldownColumns: skillSwiftColumnDescription(SwiftSkill.Verbal), skillDrilldownColumns: skillSwiftColumnDescription(SwiftSkill.Verbal), drilldownBottomComponent: function (confidential) {
                return confidential ? undefined : (_jsx(DrilldownSkillGroupTable, { category: Tab.SavilleSwiftAptitude, skillGroup: SwiftSkill.Verbal, columnGetter: skillSwiftColumnDescription(SwiftSkill.Verbal) }));
            } }),
        _d),
    _c[Tab.ImmersiveAgility] = {
        Foresight: __assign(__assign({}, commonImmersiveAgility), { backTitle: "Immersive Agility: Foresight", backUrl: "ImmersiveAgilityForesight", skillGroupDisplayName: "Foresight", heatmapDrilldownColumns: immersiveAndAgilityColumnDescription("Foresight", "averageScore", function (score) { var _a; return (_a = score === null || score === void 0 ? void 0 : score.toFixed(1)) !== null && _a !== void 0 ? _a : ""; }), skillDrilldownColumns: immersiveAndAgilityColumnDescription("Foresight", "score", function (score) { return "".concat(Math.trunc(score)); }), drilldownBottomComponent: function () { return (_jsx(ImmersiveAgilityDrilldownFactors, { category: Tab.ImmersiveAgility, skillGroup: "foresight" })); } }),
        Learning: __assign(__assign({}, commonImmersiveAgility), { backTitle: "Immersive Agility: Learning", backUrl: "ImmersiveAgilityLearning", skillGroupDisplayName: "Learning", skillGroupDonutTitle: "Score Distribution", heatmapDrilldownColumns: immersiveAndAgilityColumnDescription("Learning", "averageScore", function (score) { var _a; return (_a = score === null || score === void 0 ? void 0 : score.toFixed(1)) !== null && _a !== void 0 ? _a : ""; }), skillDrilldownColumns: immersiveAndAgilityColumnDescription("Learning", "score", function (score) { return "".concat(Math.trunc(score)); }), drilldownBottomComponent: function () { return (_jsx(ImmersiveAgilityDrilldownFactors, { category: Tab.ImmersiveAgility, skillGroup: "learning" })); } }),
        Adaptability: __assign(__assign({}, commonImmersiveAgility), { backTitle: "Immersive Agility: Adaptability", backUrl: "ImmersiveAgilityAdaptability", skillGroupDisplayName: "Adaptability", skillGroupDonutTitle: "Score Distribution", heatmapDrilldownColumns: immersiveAndAgilityColumnDescription("Adaptability", "averageScore", function (score) { var _a; return (_a = score === null || score === void 0 ? void 0 : score.toFixed(1)) !== null && _a !== void 0 ? _a : ""; }), skillDrilldownColumns: immersiveAndAgilityColumnDescription("Adaptability", "score", function (score) { return "".concat(Math.trunc(score)); }), drilldownBottomComponent: function () { return (_jsx(ImmersiveAgilityDrilldownFactors, { category: Tab.ImmersiveAgility, skillGroup: "adaptability" })); } }),
        Resilience: __assign(__assign({}, commonImmersiveAgility), { backTitle: "Immersive Agility: Resilience", backUrl: "ImmersiveAgilityResilience", skillGroupDisplayName: "Resilience", heatmapDrilldownColumns: immersiveAndAgilityColumnDescription("Resilience", "averageScore", function (score) { var _a; return (_a = score === null || score === void 0 ? void 0 : score.toFixed(1)) !== null && _a !== void 0 ? _a : ""; }), skillDrilldownColumns: immersiveAndAgilityColumnDescription("Resilience", "score", function (score) { return "".concat(Math.trunc(score)); }), drilldownBottomComponent: function () { return (_jsx(ImmersiveAgilityDrilldownFactors, { category: Tab.ImmersiveAgility, skillGroup: "resilience" })); } }),
    },
    // HoganHDS uses a Proxy to handle dynamic skill names
    _c[Tab.HoganHDS] = new Proxy({}, {
        get: function (_target, skill) {
            return getHoganHDSConfig(skill);
        },
    }),
    // HoganHPI uses a Proxy to handle dynamic skill names
    _c[Tab.HoganHPI] = new Proxy({}, {
        get: function (_target, skill) {
            return getHoganHPIConfig(skill);
        },
    }),
    // HoganMVPI uses a Proxy to handle dynamic skill names
    _c[Tab.HoganMVPI] = new Proxy({}, {
        get: function (_target, skill) {
            return getHoganMVPIConfig(skill);
        },
    }),
    _c);
